import { Injectable } from '@angular/core';
import { MatDialog, MatDialogConfig, MatDialogRef } from '@angular/material/dialog';
import { ModalWrapperComponent } from './modal-wrapper.component';
import { ComponentType } from '@angular/cdk/portal';
import { ModalWrapperServiceProps } from './types/props.types';

/**
 * Service for managing modal dialogs using a wrapper component.
 */
@Injectable({
  providedIn: 'root',
})
export class ModalWrapperService {
  private modalRef!: MatDialogRef<ModalWrapperComponent>;

  constructor(private dialog: MatDialog) {}

  /**
   * Opens a modal dialog with the specified component wrapped in ModalWrapperComponent.
   *
   * @param component The component to be rendered inside the modal.
   * @param config Configuration options for the dialog, including title and the render component props.
   *               This config object should be of type MatDialogConfig with additional properties:
   *               - dialogTitle?: string - The title of the dialog
   *               - componentProps?: Partial<T> - Props to be passed to the rendered component
   *               - events?: { [key: string]: (data: any) => void } - Events to be listened on the rendered component
   * @example
   * {
   *   dialogTitle: 'User Details',
   *   componentProps: { userId: '123', editable: true },
   *   width: '400px',
   *   height: '300px',
   *   events: {
   *     'userIdsUpdated': (data: User) => {
   *       ...code
   *     }
   *   }
   * }
   * @returns A reference to the opened dialog.
   */
  public open<T>(
    component: ComponentType<T>,
    config?: ModalWrapperServiceProps<T>
  ): MatDialogRef<ModalWrapperComponent> {
    this.modalRef = this.dialog.open(ModalWrapperComponent, {
      ...config,
      data: {
        ...config?.data,
        dialogTitle: config?.dialogTitle || '',
        renderComponent: component,
        componentProps: config?.componentProps,
        close: this.close.bind(this),
        events: config?.events,
      },
    });

    // Add a close event listener
    this.modalRef.afterClosed().subscribe((result) => {
      if (config?.events?.onClose) {
        config.events.onClose(result);
      }
    });

    return this.modalRef;
  }

  /**
   * Closes the currently open modal dialog.
   */
  public close(): void {
    if (this.modalRef) {
      this.modalRef.close();
    }
  }
}
